import { Sponsor, SponsorCategory } from '@/models/sponsor';
import { SponsorService } from '@/services/sponsors';
import { errorCatch } from '../utils';

const sponsorService = new SponsorService();

export const state = {
  sponsors: [],
  sponsorsOptions: {},
  sponsorsCategoryOptions: {},
  categories: [],
  sponsorsLoading: false,
  error: {}
};

export const mutations = {
  // Sponsor mutations
  ADD_SPONSOR(state: any, payload: Sponsor) {
    state.sponsors.push(payload);
  },

  SET_SPONSORS(state: any, payload: Sponsor[]) {
    state.sponsors = payload;
  },

  UPDATE_SPONSOR(state: any, payload: Sponsor) {
    const index = state.sponsors.findIndex(
      (sponsor: Sponsor) => sponsor.id === payload.id
    );
    state.sponsors.splice(index, 1, payload);
  },

  REMOVE_SPONSOR(state: any, payload: number) {
    const index = state.sponsors.findIndex(
      (sponsor: Sponsor) => sponsor.id === payload
    );
    state.sponsors.splice(index, 1);
  },

  // Sponsor Category mutations
  ADD_CATEGORY(state: any, payload: SponsorCategory) {
    state.categories.push(payload);
  },

  SET_CATEGORIES(state: any, payload: SponsorCategory[]) {
    state.categories = payload;
  },

  UPDATE_CATEGORY(state: any, payload: SponsorCategory) {
    const index = state.categories.findIndex(
      (category: SponsorCategory) => category.id === payload.id
    );
    state.categories.splice(index, 1, payload);
  },

  REMOVE_CATEGORY(state: any, payload: number) {
    const index = state.categories.findIndex(
      (category: SponsorCategory) => category.id === payload
    );
    state.categories.splice(index, 1);
  },

  SET_OPTIONS_CATEGORY(state: any, payload: any) {
    state.sponsorsCategoryOptions = payload;
  },

  SET_OPTIONS(state: any, payload: any) {
    state.sponsorsOptions = payload;
  },

  SET_LOADING(state: any, payload: boolean) {
    state.sponsorsLoading = payload;
  },

  SET_ERROR(state: any, error: any) {
    errorCatch(state, error);
  },

  CLEAR_ERROR(state: any) {
    state.error = {};
  }
};

export const actions = {
  clearError: ({ commit }: any) => commit('CLEAR_ERROR'),

  // Sponsor actions
  createSponsor: async ({ commit, dispatch }: any, payload: Sponsor) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService
        .createSponsor(payload)
        .then(sponsor => {
          commit('ADD_SPONSOR', sponsor);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchSponsors: async (
    { commit, dispatch }: any,
    payload: { event: string }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService
        .fetchSponsors(payload.event)
        .then(sponsors => {
          commit('SET_SPONSORS', sponsors);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchSponsor: async ({ commit, state, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService
        .fetchSponsor(payload)
        .then(sponsor => {
          const sponsors = [...state.sponsors];
          const index = sponsors.findIndex((s: Sponsor) => s.id === payload);
          if (index === -1) {
            commit('ADD_SPONSOR', sponsor);
          } else {
            commit('UPDATE_SPONSOR', sponsor);
          }
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  /** Must contain event in payload */
  updateSponsor: async ({ commit, dispatch }: any, payload: any) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService
        .updateSponsor(payload.id, payload.data)
        .then(sponsor => {
          console.log({ sponsor });

          commit('UPDATE_SPONSOR', sponsor);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  deleteSponsor: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService
        .deleteSponsor(payload)
        .then(() => {
          commit('REMOVE_SPONSOR', payload);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  optionsSponsor: async ({ commit, dispatch }: any) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService
        .optionsSponsor()
        .then((res: any) => {
          commit('SET_OPTIONS', res.actions.POST);
          resolve();
        })
        .catch((err: any) => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  // Sponsor category actions
  createCategory: async (
    { commit, dispatch }: any,
    payload: SponsorCategory
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService.categoryService
        .createCategory(payload)
        .then(category => {
          commit('ADD_CATEGORY', category);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchCategories: async (
    { commit, dispatch }: any,
    payload: { event: string }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      sponsorService.categoryService
        .fetchCategories(payload.event)
        .then(categories => {
          commit('SET_CATEGORIES', categories);
          resolve(categories);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  updateCategory: async (
    { commit, dispatch }: any,
    payload: SponsorCategory
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      sponsorService.categoryService
        .updateCategory(payload.id, payload)
        .then(res => {
          commit('UPDATE_CATEGORY', res);
          resolve(res);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  deleteCategory: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService.categoryService
        .deleteCategory(payload)
        .then(() => {
          commit('REMOVE_CATEGORY', payload);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },
  optionsCategory: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      sponsorService.categoryService
        .optionsCategory()
        .then(payload => {
          commit('SET_OPTIONS_CATEGORY', payload.actions.POST);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  }
};

export const getters = {
  getSponsorById: (state: any) => (id: number) => {
    return state.sponsors.find((sponsor: Sponsor) => sponsor.id === id);
  },

  /*getSponsorsByCategory: (state: any) => (category: SponsorCategory) => {
    return state.sponsors.filter(
      (sponsor: Sponsor) => sponsor.category?.id === category.id
    );
  },*/

  getCategoryByPriority: (state: any) => (priority: number) => {
    return state.categories.find(
      (category: SponsorCategory) => category.priority === priority
    );
  }
};
